// AI_CONTEXT: {
//   "docs": [
//     "epics/202610211834_GBG_group-member-background/tickets/GBG-000-membership-call-dedup-groundwork"
//   ],
//   "purpose": "Singleton for AJAX requests with retry, error handling, script/stylesheet/image loading and geo-header detection",
//   "version": "1.0.0",
//   "priority": "core",
//   "stability": "stable"
// }
/**
 * XHR - Singleton for AJAX requests with automatic retry and error handling
 *
 * SINGLETON: Central HTTP request utility providing promise-based API for all
 * server communication. Wraps jQuery.ajax with framework-specific enhancements.
 *
 * PUBLIC API NOTE: All public methods auto-inject caller context as first argument.
 * JSDoc @param lists show only user-provided parameters (what you actually pass).
 *
 * KEY FEATURES:
 * - Promise-based API (all methods return promises)
 * - Automatic 429 (rate limit) retry with exponential backoff
 * - Error handler chain (high priority, low priority, recoverable)
 * - Request blocking UI integration (via App.block())
 * - RESTful request support (JSON serialization)
 * - Geolocation header detection
 * - Image preloading
 *
 * ERROR HANDLING:
 * - High priority handlers run first (can recover from errors)
 * - Recoverable errors trigger automatic retry
 * - Low priority handlers run if unrecovered
 * - Optional error ignoring per request
 *
 * @class XHR
 * @extends Class
 * @singleton
 *
 * @example
 * // GET request
 * this.XHR.get('/api/events').then(function(data) {
 *     this.data.events = data.events;
 * }.bind(this));
 *
 * @example
 * // POST request with data
 * this.XHR.post('/api/orders', {
 *     eventId: 123,
 *     quantity: 2
 * }).then(function(order) {
 *     // Handle response
 * }.bind(this));
 *
 * @example
 * // Error handling
 * this.XHR.get('/api/data').catch(function(error) {
 *     this.handleError(error);
 * }.bind(this));
 */
define("XHR", [`Class`, `module`], function (Super, Self) {
	const CONTINENT_HEADER_NAME = `x-tixr-request-continent-code`;
	const COUNTRY_HEADER_NAME = `x-tixr-request-country-code`;
	const DEFAULT_CONTINENT_CODE = `NA`;
	const DEFAULT_COUNTRY_CODE = `US`;
	const errorHandlers = [];
	let continentCodeDeferred;
	let countryCodeDeferred;

	return Super.inherit({
		namespace: Self.id,
		requires: [`DOM`, `jquery`], // used for loading stylesheets
		singleton: true,
		constructor(setup) {
			const { Deferred, promises } = this;
			const images = {}; // used to archive the promises handed out for requested images
			let continentCode;
			let countryCode;

			// used to keep track of when a continent code is detected on a server response
			continentCodeDeferred = Deferred();
			countryCodeDeferred = Deferred();
			continentCode = continentCodeDeferred.promise;
			countryCode = countryCodeDeferred.promise;

			this.assign(promises, { continentCode, countryCode, images });
		},
		api: {
			/**
			 * General-purpose AJAX request with retry and error handling.
			 *
			 * @method XHR#ajax
			 * @param {Object} options - Request configuration
			 * @param {string} options.url - Request URL
			 * @param {string} [options.type='GET'] - HTTP method
			 * @param {Object} [options.data] - Request body
			 * @param {boolean} [options.block=true] - Block UI during request
			 * @param {boolean} [options.REST=false] - Use REST format (JSON)
			 * @param {boolean} [options.ignoreErrors=false] - Skip error handlers
			 * @returns {Promise}
			 */
			ajax: {
				public: true,
				defaults: {
					block: true,
					ignoreErrors: false,
					REST: false,
					type: `GET`,
				},
				value(context, options) {
					this.defaults(options, {
						body: options.data,
						ignoreUnrecoveredErrors: options.ignoreErrors,
						owner: context,
					});

					const { block, dataType, ignoreErrors, ignoreUnrecoveredErrors, owner, REST, type, url } = options;
					const jQuery = this.require(`jquery`);
					const uniqueId = this.uniqueId();
					const blockerKey = `${url} // ${uniqueId}`;

					let { body, contentType } = options;
					let recovering = false; // tracks if a potentially recoverable error occurred
					let promise;

					if (this.isObject(body)) {
						// clone the body to avoid modifying the passed object
						body = this.clone(body);
					}

					function placeRequest(adjustments) {
						return new Promise((resolve, reject) => {
							const error = (response) => reject(response);
							const success = (response, status, xhr) => {
								// check for continent code determined by BE
								const continentCode = xhr.getResponseHeader(CONTINENT_HEADER_NAME);
								const countryCode = xhr.getResponseHeader(COUNTRY_HEADER_NAME);

								if (this.isString(continentCode, 2)) {
									continentCodeDeferred.resolve(continentCode);
								}

								if (this.isString(countryCode, 2)) {
									countryCodeDeferred.resolve(countryCode);
								}

								// resolve with the parsed response body
								resolve(response);
							};
							let data = body;

							// A RESTful request is being made
							if (REST) {
								contentType = `application/json`;

								if (data !== undefined) {
									data = JSON.stringify(data);
								}
							}

							jQuery.ajax({ contentType, data, dataType, error, success, type, url });
						});
					}

					// block if necessary
					if (block) {
						promise = this.require({
							deps: [`App`],
							then: () => {
								const { App } = this;
								const { owner } = options;
								const key = blockerKey;

								App.block({ key, owner });
							},
						});
					}

					promise = this.when({
						promise,
						then: placeRequest, // make the first attempt at an ajax call
					});

					promise = this.when({
						promise,
						// request failed
						fail: (reason) => {
							// check for a 429 "server too busy, retry later" error
							const maxDuration = 10000;
							let duration = 250;

							const checkError = (xhrError) => {
								if (xhrError.status === 429) {
									let requestPromise;

									// back off requests, so the gap between them gets longer
									duration *= 2;

									if (duration > maxDuration) {
										duration = maxDuration;
									}

									// after the duration passes, retry the request
									requestPromise = this.defer({
										duration,
										then: placeRequest,
									});

									// recursively call this function on error, checking for another 429
									return this.when({
										promise: requestPromise,
										fail: checkError,
									});
								}

								// error is not a 429
								recovering = true; // keep track to potentially re-try later
								throw xhrError; // re-throw to pass through
							};

							return checkError(reason);
						},
					});

					if (!ignoreErrors) {
						// thread high priority error handlers into the promise chain
						this.each(errorHandlers, (config) => {
							if (config.priority === `high`) {
								promise = this.when({
									promise,
									owner: config.owner,
									fail: config.handler,
								});
							}
						});

						// thread possible recovery into the promise chain
						promise = this.when({
							promise,
							then: (result) => {
								if (!recovering) {
									// no errors occurred, pass through the result
									return result;
								}
								// an error was thrown above, one of the handlers recovered the flow

								// check if extra data was returned from the recovery
								if (this.isObject(body) && this.isObject(result)) {
									// merge the extra data into the original request body
									this.assign(body, result);
								}

								// recursively call ajax(), in case a second recovery is necessary
								return this.ajax({
									block: false, // no reason to block the nested operation
									body,
									contentType,
									dataType,
									ignoreUnrecoveredErrors: true, // avoid low priority error handlers firing twice
									REST,
									type,
									url,
								});
							},
						});
					}

					options.promise = promise;
					promise = this.when(options); // attach the passed async handling now, between the high priority and low priority error handlers

					if (!ignoreUnrecoveredErrors) {
						this.each(errorHandlers, (config) => {
							if (config.priority === `low`) {
								promise = this.when({
									promise,
									owner: config.owner,
									fail: config.handler,
								});
							}
						});
					}

					return this.when({
						promise,
						fin: () => {
							if (options.block) {
								// NOTE: App only loaded when block === true
								const { App } = this;
								const key = blockerKey;

								App.unblock({ key, owner });
							}
						},
					});
				},
			},

			/**
			 * Get continent code from server response headers (with fallback).
			 *
			 * @method XHR#getContinentCode
			 * @param {Object} [options] - Request options
			 * @returns {Promise<string>} Continent code (e.g., 'NA', 'EU')
			 */
			getContinentCode: {
				async: true,
				public: true,
				value(context, options) {
					const { Deferred, promises } = this;
					const { continentCode: promise } = promises;
					const deferred = Deferred();
					let success = false;

					// add fallback error handling
					this.when({
						promise,
						then: (result) => {
							success = true;
							deferred.resolve(result);
						},
					});

					// add timeout error handling
					this.defer({
						duration: 500,
						then: () => {
							if (!success) {
								const message = `Country code timeout, defaulting to: ${DEFAULT_CONTINENT_CODE}`;
								const type = `warning`;

								this.log({ message, type });
								deferred.resolve(DEFAULT_CONTINENT_CODE);
							}
						},
					});

					return deferred.promise;
				},
			},

			/**
			 * Get country code from server response headers (with fallback).
			 *
			 * @method XHR#getCountryCode
			 * @param {Object} [options] - Request options
			 * @returns {Promise<string>} Country code (e.g., 'US', 'UK')
			 */
			getCountryCode: {
				async: true,
				public: true,
				value(context, options) {
					const { Deferred, promises } = this;
					const { countryCode: promise } = promises;
					const deferred = Deferred();
					let success = false;

					// add fallback error handling
					this.when({
						promise,
						then: (result) => {
							success = true;
							deferred.resolve(result);
						},
					});

					// add timeout error handling
					this.defer({
						duration: 500,
						then: () => {
							if (!success) {
								const message = `Country code timeout, defaulting to: ${DEFAULT_COUNTRY_CODE}`;
								const type = `warning`;

								this.log({ message, type });
								deferred.resolve(DEFAULT_COUNTRY_CODE);
							}
						},
					});

					return deferred.promise;
				},
			},

			/**
			 * Resolve the raw IP country code from response headers, without
			 * getCountryCode()'s timeout fallback. Never resolves if no header arrives,
			 * letting callers correct a fallback value once the real country is known.
			 *
			 * @method XHR#whenCountryCode
			 * @returns {Promise<string>} Country code once a response header supplies it
			 */
			whenCountryCode: {
				async: true,
				public: true,
				value(context, options) {
					return this.promises.countryCode;
				},
			},

			/**
			 * Fetch current server timestamp.
			 *
			 * @method XHR#getServerTime
			 * @param {Object} [options] - Request options
			 * @returns {Promise<number>} Server timestamp
			 */
			getServerTime: {
				async: true,
				public: true,
				value(context, options) {
					return this.ajax({
						block: false, // important for use before App is ready
						url: `/ajax/wtii`,
						then: (response) => {
							return response.time;
						},
					});
				},
			},

			/**
			 * Preload an image asset.
			 *
			 * @method XHR#loadImage
			 * @param {Object} options - Image loading options
			 * @param {string} options.src - Image URL
			 * @param {boolean} [options.block=false] - Block UI during load
			 * @param {boolean} [options.force=false] - Bypass cache
			 * @returns {Promise<Image>} Loaded image element
			 */
			loadImage: {
				async: true,
				public: true,
				defaults: {
					block: false,
					force: false,
				},
				value(context, options) {
					return this.require({
						deps: [`App`, `DOM`],
						then: () => {
							const { App, Deferred, DOM, promises } = this;
							const { block, force, src } = options;
							const { body } = document;
							const { Image } = window;
							let promise = promises.images[src];

							if (!this.isPromise(promise) || force) {
								// no cached value
								const deferred = Deferred();
								const image = new Image();
								promise = deferred.promise;

								DOM.on({
									target: image,
									event: `load`,
									matchContainer: false,
									handler: (evt) => deferred.resolve(image),
								});

								DOM.on({
									target: image,
									event: `error`,
									matchContainer: false,
									handler: (evt) => deferred.reject(new Error(`Error loading image: ${src}`)),
								});

								this.defer({
									duration: 10000, // 10 second emergency fallback
									then: () => deferred.reject(new Error(`Timeout loading image: ${src}`)),
								});

								image.setAttribute(`src`, src);
								body.appendChild(image);

								promise.finally(() => {
									const target = image;

									DOM.off({ target }); // remove all associated listeners
									this.defer({
										duration: 5000,
										then: () => {
											if (image.parentNode === body) {
												body.removeChild(image); // remove the image from the document
											}
										},
									}); // defer
								});

								promises.images[src] = promise;
							}

							if (block) {
								const key = `img-${src}`;
								const owner = context;

								// block the app immediately
								App.block({ key, owner });

								// regardless of outcome, unblock
								promise.finally(() => App.unblock({ key, owner }));
							}

							return promise;
						},
					});
				},
			},

			/**
			 * Load an iframe (for payment providers, etc.).
			 *
			 * @method XHR#loadIframe
			 * @param {Object} options - Iframe loading options
			 * @param {string} options.src - Iframe URL
			 * @param {boolean} [options.block=false] - Block UI during load
			 * @param {boolean} [options.force=false] - Bypass cache
			 * @returns {Promise<HTMLIFrameElement>} Loaded iframe element
			 */
			loadIframe: {
				async: true,
				public: true,
				defaults: {
					block: false,
					force: false,
				},
				value(context, options) {
					return this.require({
						deps: [`App`, `DOM`],
						then: () => {
							var promise = this.promises.images[options.src],
								deferred,
								iframe;

							if (!this.isPromise(promise) || options.force) {
								// no cached value
								deferred = this.Deferred();
								promise = deferred.promise;
								iframe = document.createElement(`IFRAME`);

								this.DOM.on({
									target: iframe,
									event: `load`,
									matchContainer: false,
									handler: (/* evt */) => {
										this.DOM.off({
											target: iframe,
											event: `load`,
										});
										document.body.removeChild(iframe);
										iframe.style.cssText = ``;
										deferred.resolve(iframe);
									},
								});

								iframe.setAttribute(`src`, options.src);
								iframe.style.cssText = `visibility:hidden; position:fixed; z-index:-1;`;
								document.body.appendChild(iframe);

								this.promises.images[options.src] = promise;
							}

							if (options.block) {
								const key = `img-${options.src}`; // used for blocking the app

								this.App.block({
									// block the app
									owner: context,
									key,
								});

								promise = this.when({
									promise,
									fin: (img) => {
										// regardless of outcome
										this.App.unblock({
											// unblock the app
											owner: context,
											key,
										});
									},
								});
							}

							return promise;
						},
					});
				},
			},

			/**
			 * Register a global error handler for AJAX failures.
			 *
			 * @method XHR#addErrorHandler
			 * @param {Object} options - Handler configuration
			 * @param {Function} options.handler - Error handler function
			 * @param {string} [options.priority='low'] - 'high' (can recover) or 'low' (global fallback)
			 * @param {number} [options.order=10] - Execution order within priority
			 */
			addErrorHandler: {
				public: true,
				value(context, options) {
					options = this.defaults(options, {
						// Priority can be 'high' or 'low'
						// - high: will run before the owner is notified of failure. if recovery is successful, will auto-retry
						// - low: low priority is run after any passed async handlers. can't auto-retry, but useful for global uncaught errors
						priority: `low`,
						order: 10,
					});

					const { handler, priority, order } = options;
					const owner = context;

					errorHandlers.push({ handler, order, owner, priority });
					errorHandlers.sort((a, b) => a.order - b.order);
				},
			},

			/**
			 * Unregister error handlers (typically on instance destruction).
			 *
			 * @method XHR#removeErrorHandler
			 * @param {Object} [options] - Filter options
			 * @param {Object} [options.owner] - Remove handlers for this owner
			 * @param {Function} [options.handler] - Remove specific handler
			 */
			removeErrorHandler: {
				public: true,
				value(context, options) {
					options = this.defaults(options, {
						owner: this,
					});

					const query = {};

					if (!this.isNull(options.owner)) {
						query.owner = options.owner;
					}

					if (this.isFunction(options.handler)) {
						query.handler = options.handler;
					}

					this.extract(errorHandlers, query);
				},
			},

			/**
			 * Singleton lifecycle hook - called when an instance using XHR is destroyed.
			 *
			 * NOTE: This is an internal framework hook, not called by user code.
			 * Auto-invoked via registerDependency pattern.
			 *
			 * @method XHR#onDie
			 * @param {Object} dyingInstance - Instance being destroyed
			 * @private
			 */
			onDie: {
				public: true,
				private: false,
				value(context) {
					this.removeErrorHandler(context);

					this.require({
						deps: [`App`],
						then: () => {
							this.App.unblock({
								// in case a module was destroyed with pending requests
								owner: context,
							});
						},
					});
				},
			},
		},
	});
});

//# sourceMappingURL=XHR.js.map